import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Send, CheckCircle, Shield, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadCaptureSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    email: "",
    property_size: "",
    service_needed: "",
    preferred_date: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.phone || !formData.email || !formData.property_size || !formData.service_needed) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      setIsSubmitted(true);
      toast.success("Thank you! We'll contact you soon.");
      setFormData({
        full_name: "",
        phone: "",
        email: "",
        property_size: "",
        service_needed: "",
        preferred_date: "",
        message: "",
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error("Lead submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const propertySizes = [
    "Under 1,000 sq ft",
    "1,000 - 2,500 sq ft",
    "2,500 - 5,000 sq ft",
    "5,000 - 10,000 sq ft",
    "Over 10,000 sq ft",
  ];

  const services = [
    "Landscape Design",
    "Lawn Installation",
    "Garden Planting",
    "Lawn Mowing & Maintenance",
    "Hedge & Shrub Trimming",
    "Seasonal Cleanup",
    "Irrigation Setup",
    "Mulching & Soil Prep",
    "Full Property Assessment",
  ];

  if (isSubmitted) {
    return (
      <section
        data-testid="lead-capture-section"
        className="section-padding bg-white"
      >
        <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="w-20 h-20 bg-[#2D5A27] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1A2C22] font-bold mb-4">
              Thank You!
            </h2>
            <p className="text-[#4B5563] text-lg mb-8">
              We've received your request. A member of our team will contact you within 24 hours to discuss your landscaping needs.
            </p>
            <Button
              data-testid="submit-another"
              onClick={() => setIsSubmitted(false)}
              className="bg-[#2D5A27] hover:bg-[#1E3F1B] text-white h-12 px-8 rounded-full"
            >
              Submit Another Request
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="lead-capture-section"
      id="lead-form"
      className="section-padding bg-white"
    >
      <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Content */}
          <div>
            <span className="inline-flex items-center gap-2 text-[#C2410C] font-medium text-sm mb-4">
              <Send className="w-4 h-4" />
              Free Estimate
            </span>
            <h2
              data-testid="lead-capture-headline"
              className="font-heading text-3xl md:text-4xl lg:text-5xl text-[#1A2C22] font-bold mb-6"
            >
              Get Your Free Estimate Today
            </h2>
            <p className="text-[#4B5563] text-base md:text-lg mb-8 leading-relaxed">
              Fill out the form and we'll get back to you within 24 hours with a free, no-obligation estimate for your landscaping project.
            </p>

            {/* Benefits */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#2D5A27]" />
                <span className="text-[#1A2C22]">Free property assessment</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#2D5A27]" />
                <span className="text-[#1A2C22]">Detailed written estimate</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#2D5A27]" />
                <span className="text-[#1A2C22]">No obligation to proceed</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#2D5A27]" />
                <span className="text-[#1A2C22]">Response within 24 hours</span>
              </div>
            </div>

            {/* Privacy Note */}
            <div className="mt-8 flex items-start gap-3 p-4 bg-[#F3F4F1] rounded-xl">
              <Shield className="w-5 h-5 text-[#2D5A27] flex-shrink-0 mt-0.5" />
              <p className="text-[#6B7280] text-sm">
                We respect your privacy. Your information will only be used to respond to your request.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="bg-[#FAFAF8] rounded-3xl p-8 lg:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="full_name" className="text-[#1A2C22] font-medium">
                    Full Name *
                  </Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    data-testid="input-full-name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    placeholder="John Smith"
                    className="mt-2 h-12 bg-white border-gray-200 focus:ring-[#2D5A27] focus:border-[#2D5A27]"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#1A2C22] font-medium">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    data-testid="input-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                    className="mt-2 h-12 bg-white border-gray-200 focus:ring-[#2D5A27] focus:border-[#2D5A27]"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-[#1A2C22] font-medium">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  name="email"
                  data-testid="input-email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                  className="mt-2 h-12 bg-white border-gray-200 focus:ring-[#2D5A27] focus:border-[#2D5A27]"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="property_size" className="text-[#1A2C22] font-medium">
                    Property Size *
                  </Label>
                  <Select
                    value={formData.property_size}
                    onValueChange={(value) => handleSelectChange("property_size", value)}
                  >
                    <SelectTrigger
                      data-testid="select-property-size"
                      className="mt-2 h-12 bg-white border-gray-200"
                    >
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {propertySizes.map((size) => (
                        <SelectItem key={size} value={size}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="service_needed" className="text-[#1A2C22] font-medium">
                    Service Needed *
                  </Label>
                  <Select
                    value={formData.service_needed}
                    onValueChange={(value) => handleSelectChange("service_needed", value)}
                  >
                    <SelectTrigger
                      data-testid="select-service"
                      className="mt-2 h-12 bg-white border-gray-200"
                    >
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="preferred_date" className="text-[#1A2C22] font-medium">
                  Preferred Service Date
                </Label>
                <Input
                  id="preferred_date"
                  name="preferred_date"
                  data-testid="input-preferred-date"
                  type="date"
                  value={formData.preferred_date}
                  onChange={handleInputChange}
                  className="mt-2 h-12 bg-white border-gray-200 focus:ring-[#2D5A27] focus:border-[#2D5A27]"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-[#1A2C22] font-medium">
                  Additional Details
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="input-message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your landscaping project..."
                  className="mt-2 min-h-[120px] bg-white border-gray-200 focus:ring-[#2D5A27] focus:border-[#2D5A27]"
                />
              </div>

              <Button
                type="submit"
                data-testid="submit-lead-form"
                disabled={isSubmitting}
                className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white h-14 text-lg rounded-full cta-accent"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Get My Free Estimate"
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadCaptureSection;
